import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Paper,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DescriptionIcon from '@mui/icons-material/Description'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import VisibilityIcon from '@mui/icons-material/Visibility'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import SendIcon from '@mui/icons-material/Send'
import GetAppIcon from '@mui/icons-material/GetApp'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import PublicAppHeader from 'src/consumer/components/PublicAppHeader'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'
import { APP_CONSTANTS, RELATIONSHIP_OPTIONS } from 'src/consumer/constants'
import {
  getRxLinkMeta,
  getRxLinkConsentInfo,
  startContactVerification,
  verifyContactOtp,
  submitConsent,
  requestMoreAccess,
  fetchPrescriptionPdf,
} from 'src/consumer/services/rxLinksService'
import { toast } from 'react-toastify'

const ACTOR_PATIENT = 'PATIENT'
const ACTOR_CAREGIVER = 'CAREGIVER'
const CONTACT_MSISDN = 'MSISDN'
const CONTACT_EMAIL = 'EMAIL'
const CHANNEL_WHATSAPP = 'WHATSAPP'
const CHANNEL_EMAIL = 'EMAIL'
const CHANNEL_OUTCALL = 'OUTCALL'
const CHANNEL_AUTO = 'AUTO'

const CHANNEL_LABELS = {
  WHATSAPP: 'WhatsApp',
  EMAIL: 'Email',
  OUTCALL: 'Phone call',
  AUTO: 'Auto',
}

function getErrorMessage(res, policyMatchPatient = false) {
  const status = res?.status ?? res?.response?.status
  const code = res?.code ?? res?.response?.data?.code
  const message = res?.message ?? res?.response?.data?.message

  if (status === 410 || code === 410) {
    return 'This link has expired. Please request a new prescription link.'
  }
  if (status === 401 || code === 401) {
    return 'Your session expired. Please re-accept consent.'
  }
  if (status === 429 || code === 429) {
    return 'View limit reached. Please request a new link.'
  }
  if (status === 409 || code === 409) {
    return 'This link has been used too many times. Request a new link to continue.'
  }
  if (status === 403 || code === 403) {
    if (message?.toLowerCase?.().includes('contact') && policyMatchPatient) {
      return "This number/email isn't listed for the patient. Please use a registered contact or ask the patient/caregiver to share their verified number."
    }
    return message || 'Access denied.'
  }
  if (message?.includes?.('contact_verification_required')) {
    return 'Please verify your contact before viewing.'
  }
  if (message?.includes?.('verification_resend_cooldown')) {
    return 'Please wait before requesting another OTP.'
  }
  if (message?.includes?.('verification_rate_limited')) {
    return 'Too many attempts. Please try again later.'
  }
  if (message?.includes?.('verification_invalid_otp')) {
    return 'Invalid OTP. Please check and try again.'
  }
  if (message?.includes?.('verification_expired')) {
    return 'OTP expired. Please request a new one.'
  }
  return message || 'Something went wrong. Please try again.'
}

function normalizePhone(value) {
  const digits = value.replace(/\D/g, '')
  if (digits.length <= 10 && digits.startsWith('0') === false) {
    return '+91' + digits
  }
  if (digits.length > 10 && !value.trim().startsWith('+')) {
    return '+' + digits
  }
  return value.trim().startsWith('+') ? value.trim() : '+91' + digits
}

/** Show value or "—" when API returns empty or a generic placeholder (e.g. "Clinic", "Doctor"). */
function metaValue(value, fallbackLabel = null) {
  const v = value != null ? String(value).trim() : ''
  if (!v) return '—'
  const generic = ['clinic', 'doctor', 'patient', 'date']
  if (generic.includes(v.toLowerCase()) || (fallbackLabel && v.toLowerCase() === fallbackLabel.toLowerCase())) {
    return '—'
  }
  return v
}

/** Build a safe, meaningful PDF download filename from prescription metadata. */
function getPrescriptionDownloadFilename(meta) {
  if (!meta) return 'Prescription.pdf'
  const invalid = /[/\\:*?"<>|]/g
  const sanitize = (s, maxLen = 40) => {
    if (s == null || s === '') return ''
    const v = String(s).replace(invalid, '').trim()
    return v.length > maxLen ? v.slice(0, maxLen) : v
  }
  const clinic = sanitize(meta.clinicName)
  const datePart = meta.rxDate ? String(meta.rxDate).replace(invalid, '').trim().slice(0, 10) : ''
  const rxPart = meta.maskedRxId ? sanitize(meta.maskedRxId, 20) : ''
  const parts = ['Prescription']
  if (clinic && clinic.toLowerCase() !== 'clinic') parts.push(clinic)
  if (datePart) parts.push(datePart)
  else if (rxPart) parts.push(rxPart)
  const base = parts.join('_') || 'Prescription'
  return `${base}.pdf`
}

export default function RxViewerPage() {
  const { linkToken } = useParams()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const pdfContainerRef = useRef(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [consentLoadError, setConsentLoadError] = useState(null)
  const [meta, setMeta] = useState(null)
  const [consentInfo, setConsentInfo] = useState(null)
  const [consentChecked, setConsentChecked] = useState(false)
  const [actorType, setActorType] = useState(ACTOR_PATIENT)
  const [actorName, setActorName] = useState('')
  const [relationship, setRelationship] = useState(RELATIONSHIP_OPTIONS?.[0]?.value ?? '')
  const [contactType, setContactType] = useState(CONTACT_MSISDN)
  const [contactValue, setContactValue] = useState('')
  const [channel, setChannel] = useState(CHANNEL_WHATSAPP)
  const [otpSent, setOtpSent] = useState(false)
  const [otpVerified, setOtpVerified] = useState(false)
  const [sessionId, setSessionId] = useState(null)
  const [otp, setOtp] = useState('')
  const [verifyLoading, setVerifyLoading] = useState(false)
  const [consentLoading, setConsentLoading] = useState(false)
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [pdfError, setPdfError] = useState(null)
  const [legalDrawerOpen, setLegalDrawerOpen] = useState(false)
  const [legalDrawerUrl, setLegalDrawerUrl] = useState('')
  const [legalDrawerTitle, setLegalDrawerTitle] = useState('')
  const [consentReturned409, setConsentReturned409] = useState(false)
  const [requestMoreAccessLoading, setRequestMoreAccessLoading] = useState(false)
  const [requestMoreAccessSent, setRequestMoreAccessSent] = useState(false)
  const [requestMoreAccessError, setRequestMoreAccessError] = useState(null)

  useEffect(() => {
    return () => {
      if (pdfBlobUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(pdfBlobUrl)
      }
    }
  }, [pdfBlobUrl])

  const isSecure = typeof localStorage !== 'undefined' && !!localStorage.getItem('token')

  const loadMeta = useCallback(async () => {
    if (!linkToken) {
      setError('Invalid link.')
      setLoading(false)
      return
    }
    const res = await getRxLinkMeta(linkToken, isSecure)
    if (res?.data?.rxMeta) {
      setMeta(res.data.rxMeta)
      setError(null)
    } else {
      setError(getErrorMessage(res))
      setMeta(null)
    }
    setLoading(false)
  }, [linkToken, isSecure])

  const loadConsentInfo = useCallback(async () => {
    if (!linkToken) return
    setConsentLoadError(null)
    const res = await getRxLinkConsentInfo(linkToken, isSecure)
    if (res?.data?.consentInfo) {
      const info = res.data.consentInfo
      setConsentInfo(info)
      setConsentLoadError(null)
      const channels = info.verificationChannels || []
      if (channels.length && !channels.includes(channel)) {
        setChannel(channels[0])
      }
      const types = info.contactTypes || []
      if (types.length && !types.includes(contactType)) {
        setContactType(types[0])
      }
    } else {
      setConsentLoadError(getErrorMessage(res))
    }
  }, [linkToken, isSecure])

  useEffect(() => {
    loadMeta()
  }, [loadMeta])

  useEffect(() => {
    if (meta && !error) {
      loadConsentInfo()
    }
  }, [meta, error, loadConsentInfo])

  const handleStartOtp = async () => {
    const value =
      contactType === CONTACT_MSISDN ? normalizePhone(contactValue) : contactValue.trim()
    if (!value) {
      toast.error(contactType === CONTACT_MSISDN ? 'Enter a valid phone number.' : 'Enter your email.')
      return
    }
    setVerifyLoading(true)
    setOtpSent(false)
    setOtpVerified(false)
    setSessionId(null)
    const res = await startContactVerification(
      linkToken,
      { contactType, contactValue: value, channel },
      isSecure,
    )
    setVerifyLoading(false)
    if (res?.data?.verification) {
      setSessionId(res.data.verification.sessionId)
      setOtpSent(true)
      setOtp('')
      toast.success('OTP sent. Check your ' + (channel === CHANNEL_EMAIL ? 'email' : 'phone') + '.')
    } else {
      const policyMatch =
        consentInfo?.contactVerificationPolicy === 'MATCH_PATIENT_CONTACTS'
      toast.error(getErrorMessage(res, policyMatch))
    }
  }

  const handleVerifyOtp = async () => {
    if (!sessionId || !otp.trim()) {
      toast.error('Enter the OTP you received.')
      return
    }
    setVerifyLoading(true)
    const res = await verifyContactOtp(
      linkToken,
      { sessionId, otp: otp.trim() },
      isSecure,
    )
    setVerifyLoading(false)
    if (res?.data?.verification?.verified) {
      setOtpVerified(true)
      toast.success('Verified.')
    } else {
      toast.error(getErrorMessage(res))
    }
  }

  const handleSubmitConsent = async () => {
    if (!consentChecked) {
      toast.error('Please accept the declaration to continue.')
      return
    }
    if (consentInfo?.contactVerificationRequired && !sessionId) {
      toast.error('Please verify your contact with OTP first.')
      return
    }
    setConsentLoading(true)
    const relationshipLabel =
      RELATIONSHIP_OPTIONS?.find((o) => o.value === relationship)?.label ?? relationship
    const body = {
      accepted: true,
      actorType,
      ...(actorType === ACTOR_CAREGIVER && {
        actorName: actorName.trim(),
        relationship: relationshipLabel || relationship,
      }),
    }
    if (consentInfo?.contactVerificationRequired && sessionId) {
      body.verificationSessionId = sessionId
    } else if (isSecure && contactValue.trim()) {
      body.contactType = contactType
      body.contactValue =
        contactType === CONTACT_MSISDN ? normalizePhone(contactValue) : contactValue.trim()
    }
    const res = await submitConsent(linkToken, body, isSecure)
    setConsentLoading(false)
    if (res?.viewToken && res?.rxId) {
      setPdfLoading(true)
      setPdfError(null)
      try {
        const blob = await fetchPrescriptionPdf(res.rxId, res.viewToken)
        const url = URL.createObjectURL(blob)
        setPdfBlobUrl(url)
        if (isMobile && pdfContainerRef.current) {
          pdfContainerRef.current.scrollIntoView({ behavior: 'smooth' })
        }
      } catch (e) {
        setPdfError(getErrorMessage(e))
      } finally {
        setPdfLoading(false)
      }
    } else if (res?.code === 409) {
      setConsentReturned409(true)
    } else {
      toast.error(getErrorMessage(res))
    }
  }

  const handleRequestMoreAccess = async () => {
    if (!linkToken) return
    setRequestMoreAccessError(null)
    setRequestMoreAccessLoading(true)
    const res = await requestMoreAccess(linkToken, isSecure)
    setRequestMoreAccessLoading(false)
    if (res?.sent) {
      setRequestMoreAccessSent(true)
      toast.success('A new link has been sent. Please check your WhatsApp or email.')
    } else {
      const msg =
        res?.code === 400
          ? 'Complete verification first, then request a new link.'
          : res?.code === 404
            ? 'Prescription or patient not found. Please ask your clinic for a new link.'
            : res?.code === 410
              ? 'This link has expired. Please ask your clinic for a new prescription link.'
              : getErrorMessage(res)
      setRequestMoreAccessError(msg)
    }
  }

  const canSubmitConsent =
    consentChecked &&
    (!consentInfo?.contactVerificationRequired || (sessionId && otpVerified)) &&
    (actorType !== ACTOR_CAREGIVER || (actorName.trim() && relationship.trim()))

  const contactLabel =
    contactType === CONTACT_MSISDN ? 'Phone number (e.g. +91 9876543210)' : 'Email address'

  const opensLeft =
    meta && meta.maxOpens != null && meta.maxOpens > 0
      ? Math.max(0, (meta.maxOpens ?? 0) - (meta.opens ?? 0))
      : null
  const opensExhaustedFromMeta = meta && meta.maxOpens != null && meta.maxOpens > 0 && (opensLeft ?? 0) <= 0
  const showOpenLimitState = opensExhaustedFromMeta || consentReturned409

  const openLegalDrawer = (path, title) => {
    setLegalDrawerUrl(path)
    setLegalDrawerTitle(title)
    setLegalDrawerOpen(true)
  }

  const handleDownloadPdf = () => {
    if (!pdfBlobUrl) return
    const filename = getPrescriptionDownloadFilename(meta)
    const a = document.createElement('a')
    a.href = pdfBlobUrl
    a.download = filename
    a.rel = 'noopener noreferrer'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    toast.success('Download started.')
  }

  const downloadsAllowed = meta?.downloadsAllowed !== false

  if (loading) {
    return (
      <>
        <PublicAppHeader />
        <SEOComponent
          title="View prescription | AUMHUM"
          description="View your prescription"
          image={APP_CONSTANTS.APP_LOGO_LINK}
        />
        <Box
          sx={{
            minHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            background: 'linear-gradient(180deg, rgba(248,250,252,0.6) 0%, transparent 100%)',
          }}
        >
          <CircularProgress size={40} thickness={4} sx={{ color: 'primary.main', mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading prescription…
          </Typography>
        </Box>
      </>
    )
  }

  if (error && !meta) {
    return (
      <>
        <PublicAppHeader />
        <SEOComponent
          title="Prescription link | AUMHUM"
          description="View your prescription"
          image={APP_CONSTANTS.APP_LOGO_LINK}
        />
        <Box
          sx={{
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              maxWidth: 420,
              p: 4,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'error.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 32, color: 'error.main' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Unable to load prescription
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {error}
            </Typography>
          </Paper>
        </Box>
      </>
    )
  }

  const cardSx = {
    p: 2.5,
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  }

  const detailsCard = meta ? (
    <Paper elevation={0} sx={cardSx} key="details">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <LocalHospitalIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Prescription details
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 64 }}>
                Clinic
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }} color={metaValue(meta.clinicName) === '—' ? 'text.secondary' : 'text.primary'}>
                {metaValue(meta.clinicName, 'Clinic')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PersonOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 48 }}>
                Doctor
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }} color={metaValue(meta.doctorName) === '—' ? 'text.secondary' : 'text.primary'}>
                {metaValue(meta.doctorName, 'Doctor')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 64 }}>
                Patient
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {meta.patientFirstNameMasked || '—'}
                {meta.maskedRxId && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    · {meta.maskedRxId}
                  </Typography>
                )}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 48 }}>
                Date
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }} color={metaValue(meta.rxDate) === '—' ? 'text.secondary' : 'text.primary'}>
                {metaValue(meta.rxDate, 'Date')}
              </Typography>
            </Box>
            {meta.maxOpens != null && meta.maxOpens > 0 && opensLeft != null && (
              <Typography variant="caption" color="text.secondary">
                Opens left: {opensLeft} of {meta.maxOpens}
              </Typography>
            )}
            {meta.linkExpiresAt && (
              <Typography variant="caption" color="text.secondary" display="block">
                Link expires: {meta.linkExpiresAt}
              </Typography>
            )}
          </Box>
        </Paper>
  ) : null

  const consentCard = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }} key="consent">
      {consentLoadError && (
        <Alert
          severity="error"
          onClose={() => setConsentLoadError(null)}
          sx={{ borderRadius: 2 }}
        >
          {consentLoadError}
        </Alert>
      )}
      {consentInfo && (
        <Paper elevation={0} sx={cardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <VerifiedUserIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Consent
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            {consentInfo.declarationText}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
            By continuing you agree to our{' '}
            <Typography
              component="span"
              variant="body2"
              color="primary"
              sx={{ textDecoration: 'underline', cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
              onClick={() => openLegalDrawer('/terms', 'Terms of Service')}
            >
              Terms of Service
            </Typography>
            {' '}and{' '}
            <Typography
              component="span"
              variant="body2"
              color="primary"
              sx={{ textDecoration: 'underline', cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
              onClick={() => openLegalDrawer('/privacy-policy', 'Privacy Policy')}
            >
              Privacy Policy
            </Typography>
            .
          </Typography>
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              border: '2px solid',
              borderColor: consentChecked ? 'primary.main' : 'divider',
              bgcolor: consentChecked ? 'primary.50' : 'grey.50',
              transition: 'border-color 0.2s, background-color 0.2s',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  color="primary"
                  sx={{ py: 0.5, '& .MuiSvgIcon-root': { fontSize: 26 } }}
                />
              }
              label={
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  I agree to the above
                </Typography>
              }
            />
          </Box>
          {consentChecked && (
            <>
              {(consentInfo.actorTypes || []).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom sx={{ fontWeight: 500 }}>
                    I am viewing as
                  </Typography>
                  <RadioGroup
                    row
                    value={actorType}
                    onChange={(e) => setActorType(e.target.value)}
                    sx={{ gap: 1 }}
                  >
                    {consentInfo.actorTypes.includes(ACTOR_PATIENT) && (
                      <FormControlLabel value={ACTOR_PATIENT} control={<Radio size="small" />} label="Patient" />
                    )}
                    {consentInfo.actorTypes.includes(ACTOR_CAREGIVER) && (
                      <FormControlLabel value={ACTOR_CAREGIVER} control={<Radio size="small" />} label="Caregiver" />
                    )}
                  </RadioGroup>
                </Box>
              )}
              {actorType === ACTOR_CAREGIVER && (consentInfo.caregiverFields || []).includes('actorName') && (
                <TextField
                  fullWidth
                  size="small"
                  label="Your name"
                  value={actorName}
                  onChange={(e) => setActorName(e.target.value)}
                  sx={{ mt: 1.5 }}
                  variant="outlined"
                />
              )}
              {actorType === ACTOR_CAREGIVER && (consentInfo.caregiverFields || []).includes('relationship') && (
                <FormControl fullWidth size="small" sx={{ mt: 1.5 }} variant="outlined">
                  <InputLabel id="rx-relationship-label">Relationship to patient</InputLabel>
                  <Select
                    labelId="rx-relationship-label"
                    label="Relationship to patient"
                    value={relationship}
                    onChange={(e) => setRelationship(e.target.value)}
                  >
                    {RELATIONSHIP_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {consentInfo.contactVerificationRequired && (
                <Box sx={{ mt: 2.5, p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom sx={{ fontWeight: 500 }}>
                    Verify your contact to receive OTP
                  </Typography>
                  {consentInfo.contactVerificationPolicy === 'ANY_VERIFIED' && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Enter any phone or email to receive an OTP for verification.
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                    {(consentInfo.contactTypes || []).length > 1 && (
                      <RadioGroup
                        row
                        value={contactType}
                        onChange={(e) => setContactType(e.target.value)}
                        sx={{ gap: 1 }}
                      >
                        {consentInfo.contactTypes.includes(CONTACT_MSISDN) && (
                          <FormControlLabel value={CONTACT_MSISDN} control={<Radio size="small" />} label="Phone" />
                        )}
                        {consentInfo.contactTypes.includes(CONTACT_EMAIL) && (
                          <FormControlLabel value={CONTACT_EMAIL} control={<Radio size="small" />} label="Email" />
                        )}
                      </RadioGroup>
                    )}
                    <TextField
                      fullWidth
                      size="small"
                      label={contactLabel}
                      type={contactType === CONTACT_EMAIL ? 'email' : 'tel'}
                      value={contactValue}
                      onChange={(e) => setContactValue(e.target.value)}
                      placeholder={contactType === CONTACT_MSISDN ? '+91 9876543210' : 'you@example.com'}
                      variant="outlined"
                    />
                    {(consentInfo.verificationChannels || []).length > 0 && (
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Send OTP via"
                        value={channel}
                        onChange={(e) => setChannel(e.target.value)}
                        SelectProps={{ native: true }}
                        variant="outlined"
                      >
                        {(consentInfo.verificationChannels || []).map((ch) => (
                          <option key={ch} value={ch}>
                            {CHANNEL_LABELS[ch] || ch}
                          </option>
                        ))}
                      </TextField>
                    )}
                    <Button
                      variant="outlined"
                      onClick={handleStartOtp}
                      disabled={verifyLoading || !contactValue.trim()}
                      sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                    >
                      {verifyLoading ? <CircularProgress size={20} /> : 'Send OTP'}
                    </Button>
                    {otpSent && (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                          size="small"
                          label="OTP"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          inputProps={{ maxLength: 6 }}
                          sx={{ width: 120 }}
                          variant="outlined"
                        />
                        <Button
                          variant="contained"
                          onClick={handleVerifyOtp}
                          disabled={verifyLoading || otp.length < 4}
                          sx={{ textTransform: 'none' }}
                        >
                          Verify
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {!pdfBlobUrl && (
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2.5, py: 1.25, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  onClick={handleSubmitConsent}
                  disabled={!canSubmitConsent || consentLoading || opensExhaustedFromMeta}
                  startIcon={consentLoading ? <CircularProgress size={18} color="inherit" /> : <VisibilityIcon />}
                >
                  {consentLoading ? 'Opening…' : 'View prescription'}
                </Button>
              )}
            </>
          )}
        </Paper>
      )}

      {isMobile && !pdfBlobUrl && consentInfo && (
        <Button
          variant="outlined"
          fullWidth
          onClick={() => pdfContainerRef.current?.scrollIntoView({ behavior: 'smooth' })}
          startIcon={<DescriptionIcon />}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          View PDF (below)
        </Button>
      )}
    </Box>
  )

  const openLimitCard = (
    <Paper
      elevation={0}
      sx={{
        ...cardSx,
        p: 3,
        textAlign: 'center',
        maxWidth: 420,
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          bgcolor: 'warning.50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
        }}
      >
        <LinkOffIcon sx={{ fontSize: 28, color: 'warning.main' }} />
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        This link has been used too many times
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
        For your privacy, we'll send a fresh access link to your verified contact.
      </Typography>
      {requestMoreAccessError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setRequestMoreAccessError(null)}>
          {requestMoreAccessError}
        </Alert>
      )}
      {requestMoreAccessSent ? (
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          A new link has been sent. Please check your WhatsApp or email.
        </Alert>
      ) : (
        <>
          <Button
            variant="contained"
            fullWidth
            onClick={handleRequestMoreAccess}
            disabled={requestMoreAccessLoading}
            startIcon={requestMoreAccessLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            sx={{ py: 1.25, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {requestMoreAccessLoading ? 'Sending…' : 'Request New Link'}
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            If you don't receive it, ask your clinic to resend.
          </Typography>
        </>
      )}
    </Paper>
  )

  const formPanel = !pdfBlobUrl && (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 2.5,
        flex: isMobile ? 'none' : '0 0 760px',
        maxWidth: isMobile ? '100%' : 760,
        minHeight: 0,
      }}
    >
      <Box sx={{ flex: isMobile ? 'none' : '0 0 280px', minWidth: 0 }}>{detailsCard}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {showOpenLimitState ? openLimitCard : consentCard}
      </Box>
    </Box>
  )

  const pdfMinHeight = isMobile ? 420 : 400
  const rightPanel = (
    <Box
      ref={pdfContainerRef}
      sx={{
        width: '100%',
        minWidth: 0,
        flex: 1,
        minHeight: isMobile ? 400 : 0,
        height: isMobile ? 'auto' : '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {pdfLoading && (
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: isMobile ? 380 : '100%',
            flex: isMobile ? 'none' : 1,
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <CircularProgress size={36} thickness={4} sx={{ color: 'primary.main', mb: 1.5 }} />
          <Typography variant="body2" color="text.secondary">
            Loading prescription…
          </Typography>
        </Paper>
      )}
      {pdfError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {pdfError}
        </Alert>
      )}
      {pdfBlobUrl && !pdfLoading && (
        <Paper
          elevation={0}
          component="div"
          sx={{
            position: 'relative',
            width: '100%',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            height: '100%',
            minHeight: pdfMinHeight,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {downloadsAllowed && (
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                px: 1.5,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={handleDownloadPdf}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Download PDF
              </Button>
            </Box>
          )}
          <Box
            sx={{
              position: 'relative',
              flex: 1,
              minHeight: 0,
            }}
          >
            <iframe
              title="Prescription PDF"
              src={pdfBlobUrl}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </Box>
        </Paper>
      )}
    </Box>
  )

  return (
    <>
      <PublicAppHeader />
      <SEOComponent
        title="View prescription | AUMHUM"
        description="View your prescription securely"
        image={APP_CONSTANTS.APP_LOGO_LINK}
      />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'grey.50',
          pb: { xs: 4, md: 0 },
          ...(isMobile ? {} : { height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }),
        }}
      >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1600,
          mx: 'auto',
          px: { xs: 2, sm: 3, lg: 4 },
          pt: { xs: 2, sm: 3 },
          flex: isMobile ? 'none' : 1,
          minHeight: 0,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: { xs: 2, md: 2 },
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              flexShrink: 0,
            }}
          >
            Your prescription
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: { xs: 2, md: 3, lg: 4 },
              alignItems: 'stretch',
              flex: isMobile ? 'none' : 1,
              minHeight: 0,
              minWidth: 0,
            }}
          >
            {formPanel}
            <Box sx={{ flex: 1, width: '100%', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{rightPanel}</Box>
          </Box>
        </Box>
      </Box>

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={legalDrawerOpen}
        onClose={() => setLegalDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 420,
            maxWidth: '100%',
            height: isMobile ? '85vh' : '100%',
            maxHeight: isMobile ? '85vh' : '100%',
            borderRadius: isMobile ? '16px 16px 0 0' : 0,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {legalDrawerTitle}
            </Typography>
            <IconButton
              aria-label="Close"
              onClick={() => setLegalDrawerOpen(false)}
              size="small"
              sx={{ ml: 1 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {legalDrawerUrl && (
              <iframe
                title={legalDrawerTitle}
                src={
                  typeof window !== 'undefined'
                    ? `${window.location.origin}${legalDrawerUrl}`
                    : legalDrawerUrl
                }
                style={{
                  flex: 1,
                  width: '100%',
                  border: 'none',
                  minHeight: 0,
                }}
              />
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  )
}
